import { useState } from "react";
import { useProjectContent } from "../../hooks/useProjectContent";
import { LayerItem } from "../../services/types";
import { formatValue, parseMarkdown } from "../../utils/helpers";
import ConstructionAttributeTable from "./ConstructionAttributeTable";
import ContentCarousel from "./ContentCarousel";

interface ConstructionProjectProps {
    layerItem: LayerItem;
};

const ConstructionProject = ({ layerItem }: ConstructionProjectProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Destructure metric properties
    const { description, name, value, attributes, external_id } = layerItem;
    const { content } = useProjectContent();
    const itemContent = content?.find(i => i.external_id === external_id);

    const imgContent = itemContent?.content;
    const testimonials = itemContent?.testimonials;

    // Grab crash impact statement
    const crashAttribute = attributes.filter(attribute => attribute.value && attribute.name === 'Crash Impact Statement');
    const impactStatement = crashAttribute[0]?.value || null;

    return (
        <div className="mt-4 p-6">
            <div className="mb-2 border-b flex justify-between items-end">
                {/* Name */}
                <h2 className="text-lg font-bold">{name}</h2>
                {/* Fund Amount */}
                <p className="text-xs pr-2">
                    <strong>Fund Amount:</strong> {formatValue(value, 'currency')}
                </p>
            </div>
            
            <div className="grid grid-cols-5">
                <div className="pr-2 col-span-3 mb-4">
                    {/* Description with HTML */}
                    <div
                        className={`text-sm ${isExpanded ? "line-clamp-none" : "line-clamp-8"}`}
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(description) }}
                    />
                    <button
                        className="text-blue-500 hover:underline text-sm"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? "Show less" : "Show more"}
                    </button>
                </div>

                {/* Map through attributes and render ConstructionAttributeTable for each */}
                <div className="col-span-2">
                    <ConstructionAttributeTable attributes={attributes}/>
                </div>
            </div>

            {imgContent && imgContent.length > 0 && testimonials && <ContentCarousel testimonials={testimonials} beforeAfterContent={imgContent}/>}

            {/* Impact Statement */}
            {impactStatement && (
                <div className="mt-6 bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
                    <div className="bg-gray-50 px-5 py-3 border-b border-gray-300 flex items-center gap-2">
                        <svg
                            className="w-5 h-5 text-blue-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17z"
                            />
                        </svg>
                        <h3 className="text-sm font-semibold text-black uppercase tracking-wide">
                            Impact Post-Construction
                        </h3>
                    </div>
                    <div
                        className="text-sm text-black leading-relaxed px-5 py-4 whitespace-pre-line"
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(impactStatement) }}
                    />
                </div>
            )}
        </div>
    );
};

export default ConstructionProject;
